import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Avertissements, Carte, Erreur, Montant } from '../../composants/communs.tsx';
import { LIBELLE_CANAL, type PrixCree, type Produit } from './types.ts';

/**
 * Changement de prix — RG-031.
 *
 * Il n'existe pas de « modification » de prix, ni ici ni dans l'API : on
 * enregistre un nouveau prix, qui clôture le précédent la veille. Le
 * formulaire le dit, et la réponse confirme lequel a été clôturé — sinon
 * l'utilisateur croit avoir écrasé une valeur, et s'inquiète de ne pas la
 * retrouver.
 */
export function FormulairePrix({
  produit,
  boutiqueId,
  onFerme,
  onEnregistre,
}: {
  produit: Produit;
  boutiqueId: string | null;
  onFerme: () => void;
  onEnregistre: () => void;
}): ReactNode {
  const [montant, setMontant] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [surcharge, setSurcharge] = useState(false);
  const [resultat, setResultat] = useState<PrixCree | null>(null);

  const mutation = useMutation({
    mutationFn: (corps: Record<string, unknown>) =>
      appeler<PrixCree>('/prix', { methode: 'POST', corps }),
    onSuccess: (reponse) => {
      setResultat(reponse);
      setMontant('');
      onEnregistre();
    },
  });

  return (
    <Carte
      titre={`Prix — ${produit.designation}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <p className="entete__sous" style={{ marginTop: 0 }}>
        {produit.prixUnitaire === null ? (
          <>Ce produit n’a encore aucun prix</>
        ) : (
          <>
            Prix actuel <Montant valeur={produit.prixUnitaire} />
          </>
        )}{' '}
        sur le canal{' '}
        {/* Le repli garde son sens : `produit.canal` vient du serveur, et rien
            ne garantit qu'il fasse partie des canaux connus du front. */}
        {(LIBELLE_CANAL as Record<string, string | undefined>)[produit.canal] ??
          produit.canal}
        . Enregistrer un
        nouveau prix {produit.prixUnitaire === null ? '' : 'clôture celui-ci la veille : '}
        {produit.prixUnitaire === null
          ? 'rendra ce produit vendable sur ce canal.'
          : 'l’historique reste entier, et les ventes déjà passées gardent le prix de leur jour.'}
      </p>

      <form
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setResultat(null);
          mutation.mutate({
            produit_id: produit.id,
            canal: produit.canal,
            boutique_id: surcharge ? boutiqueId : null,
            montant: Number(montant),
            ...(dateDebut === '' ? {} : { date_debut: dateDebut }),
          });
        }}
      >
        <div className="champs">
          <div className="champ">
            <label htmlFor="montant">Nouveau prix (FCFA)</label>
            <input
              id="montant"
              name="montant"
              type="number"
              min={1}
              step={1}
              required
              value={montant}
              onChange={(evenement) => {
                setMontant(evenement.target.value);
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="date-debut">Applicable à partir du</label>
            <input
              id="date-debut"
              name="date_debut"
              type="date"
              value={dateDebut}
              onChange={(evenement) => {
                setDateDebut(evenement.target.value);
              }}
            />
          </div>
        </div>

        <label style={{ margin: '4px 0 12px' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 8 }}
            checked={surcharge}
            onChange={(evenement) => {
              setSurcharge(evenement.target.checked);
            }}
          />
          Prix propre à cette boutique seulement
        </label>

        <Erreur erreur={mutation.error} />
        <Avertissements liste={resultat?.avertissements} />

        {resultat?.remplace != null && (
          <div className="message" style={{ background: 'var(--fond-appui)' }}>
            {resultat.remplace.date_fin === null ? (
              // Même jour de début que le prix précédent : une correction,
              // pas un changement de tarif (voir service-prix.ts) — le
              // montant a été remplacé sur place, rien n'a été clôturé.
              <>
                Prix corrigé — l’ancien montant{' '}
                <Montant valeur={resultat.remplace.montant} /> ne s’est jamais appliqué.
              </>
            ) : (
              <>
                Ancien prix <Montant valeur={resultat.remplace.montant} /> clôturé au{' '}
                <span className="mono">{resultat.remplace.date_fin}</span>.
              </>
            )}
          </div>
        )}

        <button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : 'Enregistrer le prix'}
        </button>
      </form>

      <SeuilQuantite produit={produit} onEnregistre={onEnregistre} />
    </Carte>
  );
}

/**
 * Quantité minimale du canal — RG-034, RG-035.
 *
 * Le réglage existait côté serveur depuis le début et n'était relié à aucun
 * écran : la colonne « Qté min. » du catalogue AFFICHAIT la valeur sans
 * jamais permettre de la changer.
 *
 * La conséquence était coûteuse et silencieuse : tout canal restait à 1, donc
 * un prix de gros s'appliquait dès **une** unité. Un client pouvait acheter
 * un seul article au tarif réservé aux grosses quantités.
 *
 * Placé sous le prix, et pas dans un écran à part : le prix et le seuil sont
 * les deux moitiés de ce que veut dire un canal. « 75 000 F » ne signifie
 * rien tant qu'on ne sait pas à partir de combien.
 */
function SeuilQuantite({
  produit,
  onEnregistre,
}: {
  produit: Produit;
  onEnregistre: () => void;
}): ReactNode {
  const [quantite, setQuantite] = useState(String(produit.quantiteMinimale));
  const [enregistre, setEnregistre] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      appeler(`/produits/${produit.id}/seuils`, {
        methode: 'POST',
        corps: { canal: produit.canal, quantite_minimale: Number(quantite) },
      }),
    onSuccess: () => {
      setEnregistre(true);
      onEnregistre();
    },
  });

  const valeur = Number(quantite);
  const valide = Number.isInteger(valeur) && valeur >= 1;
  const inchange = valeur === produit.quantiteMinimale;

  return (
    <form
      style={{ marginTop: 20, borderTop: '1px solid var(--trait)', paddingTop: 16 }}
      onSubmit={(evenement) => {
        evenement.preventDefault();
        setEnregistre(false);
        if (valide) {
          mutation.mutate();
        }
      }}
    >
      <h3 style={{ marginBottom: 4 }}>Quantité minimale</h3>
      <p className="entete__sous" style={{ marginTop: 0, marginBottom: 12 }}>
        Sous cette quantité, la vente à ce canal est <strong>refusée</strong>,
        sans dérogation possible.
      </p>

      <div className="champs">
        <div className="champ">
          <label htmlFor="quantite-minimale">À partir de (unités)</label>
          <input
            id="quantite-minimale"
            type="number"
            min={1}
            step={1}
            value={quantite}
            onChange={(evenement) => {
              setQuantite(evenement.target.value);
              setEnregistre(false);
            }}
          />
        </div>
      </div>

      {/*
        L'avertissement vise le cas qui coûte de l'argent tous les jours : un
        canal de gros laissé à 1. On le dit là où il se corrige.
      */}
      {valeur === 1 && produit.canal !== 'DETAIL' && (
        <div className="avertissement">
          À 1, ce tarif s’applique dès une seule unité — y compris à un client
          qui n’achète pas en quantité.
        </div>
      )}

      <Erreur erreur={mutation.error} />

      {enregistre && (
        <div className="message" style={{ background: 'var(--fond-appui)' }}>
          Seuil enregistré. Les ventes en cours sur les appareils ne sont pas
          affectées : le contrôle s’applique à la validation.
        </div>
      )}

      <button type="submit" disabled={!valide || inchange || mutation.isPending}>
        {mutation.isPending ? 'Enregistrement…' : 'Enregistrer le seuil'}
      </button>
    </form>
  );
}
